import { Input, Label, Switch } from "@ncoder/ui";
import type { Changeset, Interaction, Part, Usage } from "@/services/multi-agent/protocol";

/**
 * The kind-specific body of an interaction card — the part that differs between the eight kinds.
 *
 * FOUR KINDS GET A BODY, AND FOUR DELIBERATELY DO NOT. The card already renders the title, the
 * consequence, the deadline, the options, free text and the submit path for every kind. A body is
 * added only where the generic card leaves the question genuinely unanswerable or under-informed:
 *
 *   provide_input        REQUIRED. It has no options, and `allow_free_text` may be false, so without
 *                        a form there is literally no way to answer it. A form built from
 *                        `input_schema` (§5.4: JSON Schema 2020-12, flat objects, ≤8 fields).
 *   approve_budget       "One line with the current spend, the ceiling, and what remains" (§5.4).
 *                        Every figure comes from `RunState.usage`, so it is real, not narrated.
 *   confirm_destructive  "Lists exactly what is destroyed by `component_id`" (§5.4). Derived from
 *                        the changeset's `op:"delete"` components — the only place that list exists
 *                        client-side.
 *   approve_tool         "Compact inline card naming the tool; expands to full args JSON" (§5.4).
 *                        An earlier pass of this work called the args unreachable. That was WRONG:
 *                        `reduceTool` folds `tool.call_start`'s name and `tool.call_end`'s `args`
 *                        into the `tool_call` part keyed on `ref`, and the interaction's
 *                        `context_ref:{kind:"tool_call", id}` is the key. `selectToolCallPart` is
 *                        the index; no second cache and no extra request.
 *   clarify / approve_plan / approve_changeset / auth_required
 *                        No body. Their content is either the options themselves or a surface that
 *                        already exists — the plan pane, the changeset board, the org OAuth flow.
 *
 * PURE PROPS, NO STORE. Same reason `InteractionCard` takes props: this has to be render-testable in
 * a DOM-less suite.
 */

export interface InteractionBodyProps {
  interaction: Interaction;
  usage?: Usage | null;
  changeset?: Changeset | null;
  /** The `tool_call` part named by an `approve_tool`'s `context_ref`. */
  toolCall?: Part | null;
  /** Values for a `provide_input` form, owned by the caller so the card can submit them. */
  inputValues?: Record<string, unknown>;
  onInputChange?: (field: string, value: unknown) => void;
}

/** One field of a flat JSON Schema object. Anything richer is out of scope per §5.4. */
interface SchemaField {
  name: string;
  title: string;
  type: "string" | "number" | "integer" | "boolean";
  description?: string;
  required: boolean;
  enumValues?: string[];
}

/**
 * Flatten `input_schema` into at most eight fields.
 *
 * Deliberately narrow. §5.4 constrains the schema to "flat objects only, ≤ 8 fields", so anything
 * nested, or a ninth field, is a schema this UI was not promised and would render wrong. Unknown
 * types are dropped rather than guessed — a `format: "date-time"` rendered as a text box that the
 * server then rejects is worse than the field being absent and the free-text box being used.
 */
export function schemaFields(inputSchema: unknown): SchemaField[] {
  if (!inputSchema || typeof inputSchema !== "object") return [];
  const schema = inputSchema as Record<string, unknown>;
  const props = schema.properties;
  if (!props || typeof props !== "object") return [];
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : [];

  return Object.entries(props as Record<string, unknown>)
    .slice(0, 8)
    .flatMap(([name, raw]) => {
      if (!raw || typeof raw !== "object") return [];
      const field = raw as Record<string, unknown>;
      const type = field.type;
      if (type !== "string" && type !== "number" && type !== "integer" && type !== "boolean") return [];
      const enumValues = Array.isArray(field.enum)
        ? (field.enum.filter((v) => typeof v === "string") as string[])
        : undefined;
      return [
        {
          name,
          title: typeof field.title === "string" && field.title ? field.title : name,
          type,
          description: typeof field.description === "string" ? field.description : undefined,
          required: required.includes(name),
          enumValues: enumValues?.length ? enumValues : undefined,
        } satisfies SchemaField,
      ];
    });
}

/** True when every required field of the schema has a usable value. */
export function inputComplete(inputSchema: unknown, values: Record<string, unknown>): boolean {
  return schemaFields(inputSchema)
    .filter((f) => f.required)
    .every((f) => {
      const value = values[f.name];
      if (f.type === "boolean") return typeof value === "boolean";
      return value !== undefined && value !== null && String(value).trim() !== "";
    });
}

function money(value: number | null | undefined): string {
  return typeof value === "number" ? `$${value.toFixed(2)}` : "—";
}

/**
 * Argument keys whose VALUE is never rendered.
 *
 * An `approve_tool` card exists so a person can decide whether a tool may run, which needs the tool
 * and the shape of its arguments — not a credential. Matched on the key name because that is all a
 * generic renderer can know; the value is replaced rather than the row dropped, so the reader can
 * still see that a secret was going to be sent.
 */
const REDACTED_KEY = /token|secret|password|credential|authorization|api[_-]?key|bearer|cookie|session/i;

/** Render args for reading: redacted, stable key order, and never a wall of text. */
export function formatToolArgs(args: unknown): string | null {
  if (args === undefined || args === null) return null;
  if (typeof args !== "object" || Array.isArray(args)) return JSON.stringify(args, null, 2);
  const entries = Object.entries(args as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
  if (!entries.length) return null;
  const safe = Object.fromEntries(
    entries.map(([key, value]) => [key, REDACTED_KEY.test(key) ? "[redacted]" : value]),
  );
  return JSON.stringify(safe, null, 2);
}

export function InteractionBody({
  interaction,
  usage,
  changeset,
  toolCall,
  inputValues = {},
  onInputChange,
}: InteractionBodyProps) {
  if (interaction.kind === "approve_tool") {
    const body = (toolCall?.body ?? {}) as Record<string, unknown>;
    const name =
      (typeof body.display_name === "string" && body.display_name) ||
      (typeof body.name === "string" && body.name) ||
      null;
    // `args` lands on `tool.call_end`; `args_text` is the streamed prefix that arrives first. The
    // streamed text is shown only while the complete object is still missing, so the card is useful
    // before the call finishes without ever showing a half-parsed object once it has.
    const argsJson = formatToolArgs(body.args);
    const streaming = typeof body.args_text === "string" && body.args_text ? body.args_text : null;
    if (!name && !argsJson && !streaming) return null;
    return (
      <div className="mt-2.5 rounded border border-border bg-muted/40 px-2.5 py-2">
        {name && (
          <p className="font-mono text-[12px] text-foreground">
            {name}
            {typeof body.category === "string" && body.category ? (
              <span className="ml-1.5 font-sans text-[11px] text-muted-foreground">{body.category}</span>
            ) : null}
          </p>
        )}
        {(argsJson || streaming) && (
          /*
           * Collapsed by default — progressive disclosure. The decision is usually made on the tool
           * name; the args are what an engineer opens when it is not.
           */
          <details className="mt-1">
            <summary className="cursor-pointer text-[12px] font-medium text-muted-foreground hover:text-foreground">
              {argsJson ? "Arguments" : "Arguments (still streaming)"}
            </summary>
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-muted-foreground">
              {argsJson ?? streaming}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (interaction.kind === "provide_input") {
    const fields = schemaFields(interaction.input_schema);
    if (!fields.length) {
      /*
       * A schema this UI cannot render is not a dead end: free text is a valid answer for every kind
       * that allows it (§5.4), and the card's own box is already on screen. Saying so beats an empty
       * form that looks broken.
       */
      return interaction.allow_free_text ? null : (
        <p className="mt-2.5 rounded border border-border bg-muted/40 px-2.5 py-1.5 text-[12px] text-muted-foreground">
          This question expects a structured answer this view cannot render yet. Open the run in the
          API to answer it.
        </p>
      );
    }
    return (
      <div className="mt-2.5 grid gap-2.5">
        {fields.map((field) => {
          const id = `ix-${interaction.interaction_id}-f-${field.name}`;
          const value = inputValues[field.name];
          return (
            <div key={field.name}>
              <Label htmlFor={id} className="text-[12px]">
                {field.title}
                {field.required && (
                  // A word, not just an asterisk — an asterisk alone is not an accessible name.
                  <span className="ml-1 text-[11px] font-normal text-muted-foreground">(required)</span>
                )}
              </Label>
              {field.description && (
                <p className="mb-1 text-[12px] leading-snug text-muted-foreground">{field.description}</p>
              )}
              {field.type === "boolean" ? (
                <Switch
                  id={id}
                  checked={value === true}
                  onCheckedChange={(checked) => onInputChange?.(field.name, checked)}
                />
              ) : field.enumValues ? (
                <select
                  id={id}
                  value={typeof value === "string" ? value : ""}
                  onChange={(e) => onInputChange?.(field.name, e.target.value)}
                  className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Choose…</option>
                  {field.enumValues.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id={id}
                  type={field.type === "string" ? "text" : "number"}
                  value={value === undefined || value === null ? "" : String(value)}
                  onChange={(e) =>
                    onInputChange?.(
                      field.name,
                      field.type === "string" ? e.target.value : e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="mt-0.5 text-[13px]"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (interaction.kind === "approve_budget") {
    /*
     * Every figure from `RunState.usage` — spent, ceiling, remaining. Never a narrated estimate: this
     * is money, and §1 of the frontend rules forbids fabricating a money figure outright.
     */
    const spent = usage?.cost_usd;
    const ceiling = usage?.budget_usd ?? null;
    const remaining =
      typeof spent === "number" && typeof ceiling === "number" ? Math.max(0, ceiling - spent) : null;
    return (
      <dl className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5 rounded border border-border bg-muted/40 px-2.5 py-2 text-[12px]">
        {[
          { label: "Spent", value: money(spent) },
          { label: "Ceiling", value: money(ceiling) },
          // An em dash rather than $0.00 when the ceiling is unknown — a reported zero and an absent
          // value must never render identically (§1).
          { label: "Remaining", value: money(remaining) },
        ].map(({ label, value }) => (
          <div key={label}>
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="tabular-nums text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
    );
  }

  if (interaction.kind === "confirm_destructive") {
    const doomed = (changeset?.components ?? []).filter((c) => c.op === "delete");
    if (!doomed.length) {
      // The interaction says something is being destroyed but the changeset on screen does not name
      // it. Saying so is better than an empty list that reads as "nothing will be deleted".
      return (
        <p className="mt-2.5 text-[12px] text-muted-foreground">
          The components being removed are not listed in the changeset on screen. Review the run's
          files before confirming.
        </p>
      );
    }
    return (
      <div className="mt-2.5 rounded border border-ma-err-line bg-ma-err-soft px-2.5 py-2">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-ma-err-text">
          {/* Components, not files — `totals.destructive` counts components (§2.10c). */}
          {doomed.length} component{doomed.length === 1 ? "" : "s"} will be deleted
        </p>
        <ul className="mt-1 space-y-0.5">
          {doomed.map((component) => (
            <li key={component.component_id} className="font-mono text-[12px] text-foreground">
              {component.full_name}
              <span className="ml-1.5 font-sans text-[11px] text-muted-foreground">
                {component.metadata_type}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
}
